import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Eye, ChevronDown, HelpCircle, Mail, Phone } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    document.title = 'Frequently Asked Questions | Clarity Lens Co';
  }, []);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'We ship all orders within 1-2 business days. Standard shipping typically takes 3-5 business days within the USA. You will receive a tracking number once your order ships.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free standard shipping on all orders over $75. Orders under $75 have a flat rate shipping fee of $5.99.'
        },
        {
          q: 'Can I track my order?',
          a: 'Absolutely! Once your order ships, you will receive an email with tracking information. You can use this to monitor your package\'s journey to your doorstep.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within the United States. We are working on expanding our shipping options to international destinations in the future.'
        }
      ]
    },
    {
      category: 'Products & Quality',
      questions: [
        {
          q: 'Are your glasses prescription-ready?',
          a: 'Our eyewear comes with non-prescription lenses. However, many of our frames can be fitted with prescription lenses by your local optician.'
        },
        {
          q: 'What materials are your frames made from?',
          a: 'We use premium materials including TR90 flexible polymer, stainless steel, and acetate. Each material is chosen for durability, comfort, and style. Visit our Materials page for more details.'
        },
        {
          q: 'Do your glasses block blue light?',
          a: 'Yes, our Blue Light Blocking Glasses feature specialized lenses that filter out harmful blue light from digital screens, reducing eye strain and fatigue.'
        },
        {
          q: 'Are your sunglasses polarized?',
          a: 'Yes, our Polarized UV Protection Sunglasses feature polarized lenses that reduce glare and provide 100% UV400 protection.'
        }
      ]
    },
    {
      category: 'Returns & Warranty',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day return policy. If you\'re not completely satisfied with your purchase, you can return it within 30 days for a full refund. Items must be in original condition with all packaging.'
        },
        {
          q: 'How do I initiate a return?',
          a: 'Simply email us at ' + CONTACT_INFO.email + ' with your order number and reason for return. We\'ll provide you with return instructions and a shipping label if applicable.'
        },
        {
          q: 'Do your products come with a warranty?',
          a: 'Yes! All our eyewear comes with a 1-year warranty against manufacturing defects. This covers issues with frames, hinges, and lens coatings under normal use.'
        },
        {
          q: 'What if I receive a damaged item?',
          a: 'If your item arrives damaged, please contact us within 48 hours with photos of the damage. We\'ll send a replacement immediately at no additional cost.'
        }
      ]
    },
    {
      category: 'Care & Maintenance',
      questions: [
        {
          q: 'How should I clean my glasses?',
          a: 'Use the included microfiber cloth and lens cleaner. Avoid paper towels or rough materials that can scratch lenses. Rinse with water before wiping to remove debris.'
        },
        {
          q: 'How do I store my eyewear properly?',
          a: 'Always store your glasses in the provided case when not in use. Keep them away from extreme heat, such as car dashboards, which can warp frames and damage lens coatings.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[200px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.aboutBanner}
            alt="FAQ"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <HelpCircle className="w-3.5 h-3.5" />
                  Help Center
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight">
                  Frequently Asked Questions
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="space-y-10">
            {faqs.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="font-display text-xl text-neutral-900 font-semibold mb-4">
                  {category.category}
                </h2>
                <div className="space-y-2">
                  {category.questions.map((faq, qIndex) => {
                    const isOpen = openIndex === `${catIndex}-${qIndex}`;
                    return (
                      <div 
                        key={qIndex}
                        className="bg-neutral-50 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(catIndex, qIndex)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-100 transition-colors"
                        >
                          <span className="font-medium text-neutral-900 pr-4">{faq.q}</span>
                          <ChevronDown className={`w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5">
                            <p className="text-neutral-600 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-neutral-900 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-semibold text-white mb-2">Still Have Questions?</h3>
            <p className="text-neutral-400 mb-6">Our team is here to help</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-full transition-colors"
              >
                <Mail className="w-4 h-4" />
                {CONTACT_INFO.email}
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 px-6 py-3 border border-neutral-700 hover:border-neutral-500 text-white font-medium rounded-full transition-colors"
              >
                <Phone className="w-4 h-4" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQPage;
